<template>
  <a-modal
    :open="visible"
    :title="`批量续费 — ${proxyIds.length} 个节点`"
    :confirm-loading="submitting"
    ok-text="确认批量续费"
    cancel-text="取消"
    :ok-button-props="{ disabled: !canSubmit }"
    @ok="onSubmit"
    @cancel="onCancel"
  >
    <a-alert
      type="info"
      show-icon
      style="margin-bottom:16px"
      :message="hint"
    />

    <a-radio-group v-model:value="mode" style="margin-bottom:8px">
      <a-radio value="preset">预设</a-radio>
      <a-radio value="custom">自定义</a-radio>
    </a-radio-group>

    <div v-if="mode === 'preset'">
      <a-radio-group v-model:value="preset">
        <a-radio value="7d">7 天</a-radio>
        <a-radio value="30d">30 天</a-radio>
        <a-radio value="1m">1 个月</a-radio>
        <a-radio value="3m">3 个月</a-radio>
      </a-radio-group>
    </div>

    <a-space v-else>
      <a-input-number
        v-model:value="customN"
        :min="1"
        :max="customUnit === 'm' ? 24 : 730"
        style="width:120px"
      />
      <a-select v-model:value="customUnit" style="width:90px">
        <a-select-option value="d">天</a-select-option>
        <a-select-option value="m">个月</a-select-option>
      </a-select>
      <span style="color:#9ca3af;font-size:12px">
        范围 1..{{ customUnit === 'm' ? 24 : 730 }}
      </span>
    </a-space>

    <a-divider />

    <div v-if="submitting || result" data-test="batch-extend-result">
      <a-spin :spinning="submitting">
        <div v-if="result" data-test="batch-extend-summary">
          <a-statistic
            title="已续费"
            :value="result.updated"
            :value-style="{ color: '#52c41a' }"
            style="display:inline-block;margin-right:32px"
          />
          <a-statistic
            title="已跳过"
            :value="result.skipped"
            :value-style="{ color: '#faad14' }"
            style="display:inline-block;margin-right:32px"
          />
          <a-statistic
            v-if="result.ws_failed"
            title="agent 离线"
            :value="result.ws_failed"
            style="display:inline-block;margin-right:32px"
          />
          <a-statistic
            v-if="result.db_errors"
            title="DB 错误"
            :value="result.db_errors"
            :value-style="{ color: '#f5222d' }"
            style="display:inline-block"
          />
        </div>
        <a-table
          v-if="result && result.results.length"
          :columns="resultColumns"
          :data-source="result.results"
          :pagination="false"
          size="small"
          row-key="proxy_id"
          style="margin-top:12px"
        >
          <template #bodyCell="{ column, record }">
            <template v-if="column.key === 'status'">
              <a-tag :color="statusColor(record.status)">{{ statusLabel(record.status) }}</a-tag>
            </template>
            <template v-else-if="column.key === 'old_expire_at'">
              <span style="font-family:ui-monospace,Menlo,monospace">{{ formatTs(record.old_expire_at) }}</span>
            </template>
            <template v-else-if="column.key === 'new_expire_at'">
              <span style="font-family:ui-monospace,Menlo,monospace;color:#52c41a">{{ formatTs(record.new_expire_at) }}</span>
            </template>
            <template v-else-if="column.key === 'reason'">
              <span v-if="record.reason" style="color:#9ca3af">{{ record.reason }}</span>
              <span v-else style="color:#9ca3af">—</span>
            </template>
          </template>
        </a-table>
      </a-spin>
    </div>
  </a-modal>
</template>

<script setup lang="ts">
import { computed, ref, watch } from 'vue';
import { message } from 'ant-design-vue';
import { batchExtendProxy, type BatchExtendResponse } from '../api/proxies';

interface BatchResultRow {
  proxy_id: number;
  status: string;
  old_expire_at?: string;
  new_expire_at?: string;
  node_id?: number;
  reason?: string;
}

const props = defineProps<{ visible: boolean; proxyIds: number[] }>();
const emit = defineEmits<{
  (e: 'update:visible', v: boolean): void;
  (e: 'success', payload: { updated: number; skipped: number; ws_failed: number; db_errors: number }): void;
}>();

const mode = ref<'preset' | 'custom'>('preset');
const preset = ref<'7d' | '30d' | '1m' | '3m'>('7d');
const customN = ref<number>(7);
const customUnit = ref<'d' | 'm'>('d');

const submitting = ref(false);
const result = ref<BatchExtendResponse | null>(null);

// Soft cap mirrors the backend's 200. We hard-refuse > 200
// so the operator gets an immediate error instead of a 400.
const BATCH_LIMIT = 200;
const hint = computed(() => {
  if (props.proxyIds.length > BATCH_LIMIT) {
    return `已选 ${props.proxyIds.length} 个节点，超过单次上限 ${BATCH_LIMIT}，请减少勾选数量后重试。`;
  }
  return `将向 ${props.proxyIds.length} 个节点同时下发续费命令 — 每条独立结果，已过期的会被跳过。`;
});

const duration = computed<string>(() => {
  if (mode.value === 'preset') return preset.value;
  const n = customN.value;
  if (!n || n < 1) return '';
  return `${n}${customUnit.value}`;
});

const canSubmit = computed(() => {
  if (submitting.value) return false;
  if (props.proxyIds.length === 0) return false;
  if (props.proxyIds.length > BATCH_LIMIT) return false;
  return duration.value !== '';
});

watch(
  () => props.visible,
  (v) => {
    if (v) {
      // Reset the form so re-opening the modal doesn't show stale
      // results from the previous batch.
      mode.value = 'preset';
      preset.value = '7d';
      customN.value = 7;
      customUnit.value = 'd';
      result.value = null;
      submitting.value = false;
    }
  },
);

const resultColumns = [
  { title: '节点 ID', dataIndex: 'proxy_id', key: 'proxy_id', width: 80 },
  { title: '节点', dataIndex: 'node_id', key: 'node_id', width: 70 },
  { title: '状态', dataIndex: 'status', key: 'status', width: 90 },
  { title: '原到期', dataIndex: 'old_expire_at', key: 'old_expire_at', width: 160 },
  { title: '新到期', dataIndex: 'new_expire_at', key: 'new_expire_at', width: 160 },
  { title: '原因', dataIndex: 'reason', key: 'reason' },
];

const statusColor = (s: string) => {
  switch (s) {
    case 'updated': return 'green';
    case 'skipped': return 'orange';
    case 'db_error': return 'red';
    default: return 'default';
  }
};
const statusLabel = (s: string) => {
  switch (s) {
    case 'updated': return '已续费';
    case 'skipped': return '已跳过';
    case 'db_error': return 'DB 错误';
    default: return s;
  }
};

const formatTs = (s?: string) => {
  if (!s) return '—';
  return s.replace('T', ' ').replace(/\+\d{2}:\d{2}$|Z$/, '');
};

const onCancel = () => {
  if (submitting.value) return; // don't allow close mid-batch
  emit('update:visible', false);
};

const onSubmit = async () => {
  if (!canSubmit.value) return;
  submitting.value = true;
  result.value = null;
  try {
    const r = await batchExtendProxy(props.proxyIds, duration.value);
    result.value = r.data;
    emit('success', {
      updated: r.data.updated,
      skipped: r.data.skipped,
      ws_failed: r.data.ws_failed,
      db_errors: r.data.db_errors,
    });
  } catch (e: any) {
    const errCode = e?.response?.data?.error?.code;
    const msg = e?.response?.data?.error?.message || e?.message || '批量续费失败';
    if (errCode === 'INVALID_INPUT') {
      message.error(`参数错误: ${msg}`);
    } else {
      message.error(msg);
    }
  } finally {
    submitting.value = false;
  }
};
</script>
